/**
 * Démo guidée — visite automatisée de bout en bout pour présentation / formation.
 * Navigation assistée : la visite déplace l'utilisateur à travers les modules clés
 * avec une carte explicative, en avançant manuellement ou automatiquement.
 * Self-contained (aucune lib externe) ; monté globalement dans DashboardLayout.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

interface Step {
  route: string;
  title: string;
  body: string;
}

const STEPS: Step[] = [
  { route: '/', title: 'Tableau de bord', body: 'Vue d’ensemble de votre activité : contenu, engagement, tâches et raccourcis.' },
  { route: '/content', title: 'Contenu', body: 'Créez, éditez et faites valider vos publications (workflow HIL).' },
  { route: '/calendar', title: 'Calendrier', body: 'Planifiez vos publications multi-réseaux et suivez votre programmation.' },
  { route: '/crm/contacts', title: 'CRM & Leads', body: 'Contacts, entreprises, pipeline kanban, relances et timeline d’activité.' },
  { route: '/outreach', title: 'Email Outreach', body: 'Séquences multi-étapes trackées, campagnes, modèles et boîte unifiée.' },
  { route: '/analytics', title: 'Analytics & IA', body: 'Performances, insights IA, prédictions ML et objectifs.' },
  { route: '/projects', title: 'Projets & Collaboration', body: 'Plan de lancement IA, checklist, équipe et partage d’accès granulaire.' },
  { route: '/settings/ai-registry', title: 'Registre IA', body: 'Agents, skills, prompts, modèles et outils — le cerveau IA de la plateforme.' },
  { route: '/settings/subscription', title: 'Abonnement', body: 'Votre plan, vos plafonds, vos crédits IA et le dépassement.' },
  { route: '/settings/referral', title: 'Parrainage & Bons', body: 'Parrainez, gagnez des crédits IA, et utilisez des bons de réduction.' },
];

export function DemoTour() {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goto = useCallback(
    (idx: number) => {
      const clamped = Math.max(0, Math.min(STEPS.length - 1, idx));
      setI(clamped);
      navigate(STEPS[clamped].route);
    },
    [navigate],
  );

  const start = () => {
    setActive(true);
    setI(0);
    navigate(STEPS[0].route);
  };
  const stop = useCallback(() => {
    setActive(false);
    setAuto(false);
    if (timer.current) clearTimeout(timer.current);
  }, []);

  // Auto-advance.
  useEffect(() => {
    if (!active || !auto) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (i >= STEPS.length - 1) {
        stop();
      } else {
        goto(i + 1);
      }
    }, 4500);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [active, auto, i, goto, stop]);

  if (!active) {
    return (
      <button
        type="button"
        onClick={start}
        aria-label="Lancer la démo guidée"
        className="fixed bottom-4 left-4 z-40 flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-2 text-sm font-medium text-foreground shadow-elevation-2 transition hover:bg-surface-2"
      >
        🎬 Démo guidée
      </button>
    );
  }

  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  return (
    <div
      role="dialog"
      aria-label="Démo guidée"
      className="fixed inset-x-0 bottom-4 z-50 mx-auto w-[min(92vw,32rem)] rounded-token-lg border border-border bg-surface p-4 shadow-elevation-3"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
          Démo · {i + 1}/{STEPS.length}
        </span>
        <button
          type="button"
          onClick={stop}
          aria-label="Fermer la démo"
          className="rounded-token px-2 py-0.5 text-sm text-muted hover:bg-surface-2"
        >
          ✕
        </button>
      </div>
      <h3 className="mt-1 text-lg font-semibold text-foreground">{step.title}</h3>
      <p className="mt-1 text-sm text-muted">{step.body}</p>

      <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-surface-2">
        <div
          className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all"
          style={{ width: `${((i + 1) / STEPS.length) * 100}%` }}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-1.5 text-xs text-muted">
          <input
            type="checkbox"
            checked={auto}
            onChange={(e) => setAuto(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-border accent-primary"
          />
          Lecture auto
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => goto(i - 1)}
            disabled={i === 0}
            className="rounded-token border border-border px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-surface-2 disabled:opacity-40"
          >
            Précédent
          </button>
          {last ? (
            <button
              type="button"
              onClick={stop}
              className="rounded-token bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Terminer
            </button>
          ) : (
            <button
              type="button"
              onClick={() => goto(i + 1)}
              className="rounded-token bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Suivant
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
